import { BrowserRouter } from 'react-router-dom'
import { ReactKeycloakProvider } from '@react-keycloak/web'
import { useState } from 'react'
import { ApolloClient, ApolloProvider, InMemoryCache } from '@apollo/client'
import { createUploadLink } from 'apollo-upload-client'
import { setContext } from '@apollo/client/link/context'
import { useDispatch, useSelector } from 'react-redux'
import { CircularProgress } from '@mui/material'
import LoadingModal from 'src/components/common/LoadingModal'
import CheckUser from 'src/components/common/CheckUser'
import CheckMobile from 'src/components/common/CheckMobile'
import RegistrationMain from 'src/components/Registration/RegistrationMain'
import {
  setRiskScoreTour,
  setRiskScoreTourForInput,
  showNotificationMessage,
  userinfo,
  username,
} from 'src/actions'
import UnAuthorized from 'src/components/common/UnAuthorized'
import './App.css'
import keycloak from 'src/utils/commonMethods/keycloak'
import { getLoggedInUserData } from 'src/services/individualService'
import QRReport from 'src/components/CustomerQRReport/QRReport'
import CRHealthReport from 'src/components/CustomerQRReport/CRHealthReport/CRHealthReport'
import BuyPolicyLayout from 'src/components/BuyPolicy/layout/BuyPolicyLayout'
import { isTourCompleted } from './services/productTourServices'
import Notification from 'src/components/common/Notification'
import { NOTIFICATION_TYPES } from 'src/utils/constants/common'
import KhsLayout from 'src/components/BuyPolicy/layout/KhsLayout'
import {
  CLIENT_IS,
  FETCH_USER_NOTIFICATION,
  IS_ROUTES,
  KEYCLOACK_EVENTS,
  REGISTER,
} from 'src/utils/constants/AppConstants'
import {
  REACT_TOKEN,
  SXP_BOTIDID,
} from 'src/utils/constants/SessionStorageConstants'
import { getSessionStorageItem } from 'src/utils/commonMethods/encryptDecrypt'

const App = () => {
  const [tokenReady, setTokenReady] = useState(false)
  const [userFetched, setUserFetched] = useState(false)
  const [loading, setLoading] = useState(false)

  const dispatch = useDispatch()

  const urlLast = window?.location?.href?.split('/')
  const temp = urlLast[urlLast?.length - 1]

  const setTokens = () => {
    const { token, refreshToken, idTokenParsed } = keycloak
    if (token && refreshToken && idTokenParsed) {
      sessionStorage.setItem(REACT_TOKEN, token)
      dispatch(username(idTokenParsed?.preferred_username))
      fetchUserData(idTokenParsed?.preferred_username, token)
    }
  }

  const setRefreshTokens = () => {
    const { token, refreshToken, idTokenParsed } = keycloak
    if (token && refreshToken && idTokenParsed) {
      sessionStorage.setItem(REACT_TOKEN, token)
    }
  }

  const refreshAccessToken = () => {
    keycloak
      .updateToken(50)
      .success((refreshed) => {
        if (refreshed) {
          setRefreshTokens()
        }
      })
      .error(() => {
        sessionStorage.clear()
        keycloak.logout()
      })
  }

  const handleEvent = (event) => {
    if (event === KEYCLOACK_EVENTS?.ON_AUTH_SUCCESS) {
      setTokens()
    }

    if (event === KEYCLOACK_EVENTS?.ON_TOKEN_EXPIRED) {
      refreshAccessToken()
    }

    if (event === KEYCLOACK_EVENTS?.ON_AUTH_LOGOUT) {
      sessionStorage.clear()
    }
  }

  const fetchUserData = async (userName, token) => {
    setLoading(true)
    const client = getSessionStorageItem('sub-client')
    const response = await getLoggedInUserData(token, client)

    try {
      setLoading(false)
      const responseUserInfo = {
        userData: {
          ...response?.res?.data,
          parentClientId: response?.res?.data?.clientId,
          clientId:
            response?.res?.data?.corporateAdminFor ||
            response?.res?.data?.clientId,
        },
      }

      dispatch(userinfo(responseUserInfo))
      setTokenReady(true)
      setUserFetched(true)
      fetchTourDetails(token, responseUserInfo?.userData?.userName)
    } catch (err) {
      setLoading(false)
      let num = response?.res?.data?.status
      if (400 <= num && num <= 499) {
        dispatch(
          showNotificationMessage({
            type: NOTIFICATION_TYPES?.ERROR,
            message: FETCH_USER_NOTIFICATION?.TRY_AGAIN_AFTER_SOMETIME,
          })
        )
      } else {
        dispatch(
          showNotificationMessage({
            type: NOTIFICATION_TYPES?.ERROR,
            message: FETCH_USER_NOTIFICATION?.PLEASE_RELOAD_TRY_AGAIN,
          })
        )
      }
    }
  }

  const fetchTourDetails = async (token, userName) => {
    setLoading(true)
    const response = await isTourCompleted(token, userName)
    try {
      setLoading(false)
      if (response?.status !== 200) {
        dispatch(
          showNotificationMessage({
            type: NOTIFICATION_TYPES?.ERROR,
            message: FETCH_USER_NOTIFICATION?.TRY_AGAIN_AFTER_SOMETIME,
          })
        )
      }
      const responseData = response?.data
      // success
      if (responseData?.success) {
        const isNewTourUser = responseData?.tourCompletedDetails?.tourCompleted
        if (!isNewTourUser) {
          dispatch(setRiskScoreTour(true))
          dispatch(setRiskScoreTourForInput(true))
        }
      } else {
        dispatch(
          showNotificationMessage({
            type: NOTIFICATION_TYPES?.ERROR,
            message: responseData?.message,
          })
        )
      }
    } catch (err) {
      setLoading(false)
      dispatch(
        showNotificationMessage({
          type: NOTIFICATION_TYPES?.ERROR,
          message:
            FETCH_USER_NOTIFICATION?.ERROR_ENCOUNTERED_WHILE_FETCHING_POLICY_DETAILS,
        })
      )
    }
  }

  const authLink = setContext((_, { headers }) => {
    // get the authentication token from local storage if it exists
    const token = sessionStorage.getItem(REACT_TOKEN)
    // return the headers to the context so httpLink can read them
    // dispatch(userinfo(res.data.data[0]))
    return {
      headers: {
        ...headers,
        authorization: `Bearer ${token}`,
      },
    }
  })

  const client = new ApolloClient({
    link: authLink?.concat(
      createUploadLink({
        uri: `${process?.env?.REACT_APP_GATEWAY_URL}/paisaplan`,
      })
    ),
    cache: new InMemoryCache({
      addTypename: false,
    }),
  })
  let user = useSelector((state) => state?.userInfo)

  sessionStorage.setItem(SXP_BOTIDID, user?.userName)

  const isUrlFromScanner = () => {
    return urlLast?.includes(IS_ROUTES?.QCRREPORT)
  }

  const isCRHealthReport = () => {
    return urlLast?.includes(IS_ROUTES?.CRHEALTHREPORT)
  }

  const isBuyPolicy = () => {
    return urlLast?.includes(IS_ROUTES?.BUYPOLICY)
  }

  const isKhs = () => {
    return urlLast?.includes(IS_ROUTES?.KHS)
  }

  const getRoutes = () => {
    if (isUrlFromScanner()) {
      return (
        <BrowserRouter>
          <QRReport />
        </BrowserRouter>
      )
    } else if (isCRHealthReport()) {
      return (
        <BrowserRouter>
          <CRHealthReport />
        </BrowserRouter>
      )
    } else if (isBuyPolicy()) {
      return (
        <BrowserRouter>
          <BuyPolicyLayout />
        </BrowserRouter>
      )
    } else if (isKhs()) {
      return (
        <BrowserRouter>
          <KhsLayout />
        </BrowserRouter>
      )
    } else {
      return (
        <>
          {temp === REGISTER ? (
            <BrowserRouter>
              <RegistrationMain />
            </BrowserRouter>
          ) : (
            <ReactKeycloakProvider
              initOptions={{
                onLoad: 'login-required',
                checkLoginIframe: false,
              }}
              authClient={keycloak}
              onEvent={handleEvent}
            >
              {tokenReady &&
                userFetched &&
                (user?.userInfo?.userData?.policyHolderType !==
                CLIENT_IS?.CORPORATE ? (
                  <ApolloProvider client={client}>
                    <BrowserRouter>
                      <div className='App'>
                        {!user?.userInfo?.userData?.mobileNumber ||
                        user?.userInfo?.userData?.mobileNumber === '' ||
                        !user?.userInfo?.userData?.emailId ||
                        user?.userInfo?.userData?.emailId === '' ? (
                          <CheckMobile
                            mobileCheck={user?.userInfo?.userData?.mobileNumber}
                            emailCheck={user?.userInfo?.userData?.emailId}
                            totalUserDetails={user?.userInfo}
                          />
                        ) : (
                          <CheckUser />
                        )}
                      </div>
                    </BrowserRouter>
                  </ApolloProvider>
                ) : (
                  <UnAuthorized />
                ))}
            </ReactKeycloakProvider>
          )}
        </>
      )
    }
  }

  return (
    <>
      <LoadingModal loading={loading} />
      <Notification />
      {loading && (
        <div className='loadingContainer'>
          <CircularProgress />
        </div>
      )}
      {getRoutes()}
    </>
  )
}

export default App
